<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';

import { fetchCsrfBootstrap } from '@/api/auth.js';
import { api } from '@/api/client.js';
import Banner from '@/components/ds/Banner.vue';
import Button from '@/components/ds/Button.vue';
import Field from '@/components/ds/Field.vue';
import Input from '@/components/ds/Input.vue';
import Modal from '@/components/ds/Modal.vue';
import Select from '@/components/ds/Select.vue';
import { errorIssueMessage, safeIssueUrl } from '@/utils/errorIssue.js';

const props = defineProps({
  appId: { type: String, required: true },
  fingerprint: { type: String, required: true },
});

const open = ref(false);
const loading = ref(false);
const submitting = ref(false);
const error = ref('');
const data = ref(null);
const title = ref('');
const description = ref('');
const mode = ref('create');
const existingIssueIid = ref('');
const needsRefresh = ref(false);
const report = computed(() => data.value?.report);
const target = computed(() => data.value?.target);
const issueUrl = computed(() => safeIssueUrl(report.value?.issueUrl));
const stateLabel = computed(() => ({ pending: 'Pending delivery', sent: 'Reported in GitLab', failed: 'Delivery failed' }[report.value?.state]));
const canSubmit = computed(() => data.value?.available && !needsRefresh.value && !loading.value && !submitting.value
  && (!report.value || report.value.state === 'failed')
  && (mode.value === 'link'
    ? Number.isSafeInteger(Number(existingIssueIid.value)) && Number(existingIssueIid.value) > 0
    : title.value.trim().length > 0 && description.value.trim().length > 0));
let context = 0;
let readController;
let writeController;
let pollTimer;

function stopPolling() {
  clearTimeout(pollTimer);
}

function schedulePoll() {
  stopPolling();
  if (open.value && report.value?.state === 'pending') {
    pollTimer = setTimeout(() => refresh(), 3000);
  }
}

function cancelRequests() {
  context++;
  stopPolling();
  readController?.abort();
  writeController?.abort();
}

async function refresh() {
  if (loading.value || submitting.value) return;
  const version = context;
  readController = new AbortController();
  loading.value = true;
  error.value = '';
  try {
    const response = await api.admin.errorIssue.get({
      appId: props.appId, fingerprint: props.fingerprint, signal: readController.signal,
    });
    if (version !== context) return;
    if (!response?.ok) throw new Error('Invalid issue response');
    data.value = response;
    title.value = response.preview?.title ?? '';
    description.value = response.preview?.description ?? '';
    needsRefresh.value = false;
  } catch {
    if (version === context) error.value = 'Could not load GitLab report details. Please refresh and try again.';
  } finally {
    if (version === context) {
      loading.value = false;
      schedulePoll();
    }
  }
}

async function submit() {
  if (!canSubmit.value) return;
  const version = context;
  // Capture the reviewed destination before fetching CSRF or making any request.
  const payload = {
    appId: props.appId,
    fingerprint: props.fingerprint,
    title: mode.value === 'link' ? data.value.preview.title : title.value.trim(),
    description: mode.value === 'link' ? data.value.preview.description : description.value,
    expectedApiUrl: target.value.apiUrl,
    expectedReleaseSha: data.value.preview?.releaseSha ?? null,
    expectedProjectId: target.value.projectId,
    expectedBranch: target.value.trackedBranch,
    ...(mode.value === 'link' ? { existingIssueIid: Number(existingIssueIid.value) } : {}),
  };
  writeController = new AbortController();
  const signal = writeController.signal;
  stopPolling();
  submitting.value = true;
  error.value = '';
  try {
    const { csrfToken } = await fetchCsrfBootstrap();
    if (version !== context) return;
    const response = await api.admin.errorIssue.submit({ ...payload, csrf: csrfToken, signal });
    if (version !== context) return;
    if (!response?.ok || !response.report) throw new Error('Invalid issue response');
    data.value = { ...data.value, report: response.report };
  } catch (err) {
    if (version !== context) return;
    if (err?.code === 'OPTIMISTIC_CONCURRENCY_CONFLICT') {
      needsRefresh.value = true;
      error.value = 'The GitLab destination or observed version changed. Refresh the preview and review it before submitting again.';
    } else {
      error.value = err?.details?.code
        ? errorIssueMessage(err.details.code)
        : 'Could not confirm the report. Refresh its status or retry; the same error will keep its existing report.';
    }
  } finally {
    if (version === context) {
      submitting.value = false;
      schedulePoll();
    }
  }
}

watch(() => [props.appId, props.fingerprint], () => {
  cancelRequests();
  open.value = false;
  loading.value = false;
  submitting.value = false;
  data.value = null;
  error.value = '';
  title.value = '';
  description.value = '';
  existingIssueIid.value = '';
  mode.value = 'create';
  needsRefresh.value = false;
  refresh();
}, { immediate: true });
watch(open, (isOpen) => {
  if (isOpen && report.value && !submitting.value) refresh();
  else schedulePoll();
});
onBeforeUnmount(cancelRequests);
</script>

<template>
  <section
    class="error-issue"
    aria-label="GitLab report"
    data-test="error-issue"
  >
    <h3>GitLab report</h3>
    <div class="error-issue__actions">
      <a
        v-if="issueUrl"
        :href="issueUrl"
        target="_blank"
        rel="noopener noreferrer"
        data-test="error-issue-link"
      >View report #{{ report.issueIid }}</a>
      <Button
        size="sm"
        :loading="loading && !data"
        data-test="error-issue-open"
        @click="open = true"
      >
        {{ report ? 'View report details' : 'Report in GitLab' }}
      </Button>
      <span
        v-if="report"
        role="status"
        data-test="error-issue-state"
      >{{ stateLabel }}</span>
    </div>
    <p
      v-if="!report && data && !data.available"
      class="error-issue__hint"
    >
      {{ errorIssueMessage(data.code) }}
    </p>
  </section>

  <Modal
    :open="open"
    title="Report error in GitLab"
    subtitle="Review the destination and content before sending."
    size="lg"
    @close="open = false"
  >
    <div class="error-issue__form">
      <Banner
        v-if="error"
        tone="danger"
      >
        {{ error }}
      </Banner>
      <p
        v-if="loading && !data"
        role="status"
      >
        Loading GitLab report details…
      </p>
      <template v-if="data">
        <p
          v-if="target"
          class="error-issue__destination"
          data-test="error-issue-target"
        >
          <strong>{{ target.fullPath }}</strong> · Project {{ target.projectId }} · Branch {{ target.trackedBranch }}
          <span>GitLab instance: {{ target.apiUrl }}</span>
          <span v-if="target.labels">Labels: {{ target.labels }}</span>
        </p>
        <Banner
          v-if="!data.available && report?.state !== 'sent'"
          tone="warning"
        >
          {{ errorIssueMessage(data.code) }}
        </Banner>
        <Banner
          v-if="report"
          :tone="report.state === 'sent' ? 'success' : report.state === 'failed' ? 'danger' : 'info'"
          :title="stateLabel"
        >
          <template v-if="report.state === 'pending'">
            Your report is saved and queued. You can close this dialog; delivery continues in the background.
          </template>
          <template v-else-if="report.state === 'failed'">
            {{ errorIssueMessage(report.lastErrorCode) }} Retrying keeps the original destination and report content.
          </template>
          <template v-else>
            This error remains linked to its original GitLab project, even if the app configuration changes.
            <a
              v-if="issueUrl"
              :href="issueUrl"
              target="_blank"
              rel="noopener noreferrer"
            >View report #{{ report.issueIid }}</a>
          </template>
        </Banner>
        <Field
          v-if="(!report || report.state === 'failed') && data.available"
          label="Action"
          html-for="error-issue-action"
        >
          <Select
            id="error-issue-action"
            v-model="mode"
            :disabled="submitting"
            :options="[{ value: 'create', label: report ? 'Retry original report' : 'Create a new incident' }, { value: 'link', label: 'Link an existing issue or incident' }]"
            data-test="error-issue-action"
          />
        </Field>
        <Field
          v-if="(!report || report.state === 'failed') && data.available && mode === 'link'"
          label="Existing issue or incident number"
          required
          hint="Use the number from this GitLab project, for example 123. Linking does not change its type or content."
          html-for="error-issue-iid"
        >
          <Input
            id="error-issue-iid"
            v-model="existingIssueIid"
            type="number"
            min="1"
            step="1"
            :disabled="submitting"
            data-test="error-issue-iid"
          />
        </Field>
        <template v-if="(report || data.available) && (report || mode === 'create')">
          <Field
            label="Title"
            required
            html-for="error-issue-title"
          >
            <Input
              id="error-issue-title"
              v-model="title"
              maxlength="200"
              :readonly="!!report"
              :disabled="submitting"
              data-test="error-issue-title"
            />
          </Field>
          <Field
            label="Description"
            required
            hint="Review the prepared context and remove any information you do not want to share in GitLab. Markdown is supported."
            html-for="error-issue-description"
          >
            <textarea
              id="error-issue-description"
              v-model="description"
              class="error-issue__description"
              rows="12"
              maxlength="20000"
              :readonly="!!report"
              :disabled="submitting"
              data-test="error-issue-description"
            />
          </Field>
        </template>
      </template>
    </div>
    <template #footer>
      <Button @click="open = false">
        Close
      </Button>
      <Button
        v-if="error || !data?.available || report?.state === 'pending'"
        :loading="loading"
        :disabled="submitting"
        data-test="error-issue-refresh"
        @click="refresh"
      >
        Refresh status
      </Button>
      <Button
        v-if="!report || report.state === 'failed'"
        variant="primary"
        :disabled="!canSubmit"
        :loading="submitting"
        data-test="error-issue-submit"
        @click="submit"
      >
        {{ mode === 'link' ? 'Link report' : report ? 'Retry delivery' : 'Create incident' }}
      </Button>
    </template>
  </Modal>
</template>

<style scoped>
.error-issue {
  margin: var(--space-5) 0;
}

.error-issue h3 {
  margin: 0 0 var(--space-2);
  font-size: var(--text-sm);
}

.error-issue__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-3);
  font-size: var(--text-sm);
}

.error-issue__form {
  display: grid;
  gap: var(--space-4);
}

.error-issue__destination {
  display: grid;
  gap: var(--space-1);
  margin: 0;
  font-size: var(--text-sm);
  overflow-wrap: anywhere;
}

.error-issue__hint {
  color: var(--fg-secondary);
  font-size: var(--text-xs);
}

.error-issue__description {
  width: 100%;
  padding: var(--space-3);
  color: var(--fg-primary);
  background: var(--bg-canvas);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-md);
  font: var(--text-sm) var(--font-mono);
  resize: vertical;
}
</style>
